{
    "common": {
        "or": "or",
        "logo_alt": "Logo",
        "loading": "Loading",
        "internet_restored": "Internet connection restored",
        "internet_lost": "Internet connection lost",
        "new_version_available": "A new version of the application is available. The app will be updated in 10 seconds.",
        "app_updated": "Application updated to the latest version."
    },
    "login": {
        "welcome": "How would you like to log in?",
        "no_account": "New to Medendo?",
        "start_trial_link": "Start your free trial",
        "finish_setup_message": "Your account is ready. Log in to finish setting up two-factor authentication.",
        "email_step": {
            "email_label": "Enter your email",
            "next_button": "Next",
            "invalid_email": "Invalid email address"
        },
        "refer_to_email_step": {
            "message": "An email has been sent to: <1>{{email}}</1><2/><3/>Follow the instructions in your email<4/>You can close this window."
        },
        "mfa_step": {
            "title": "Enter the verification code",
            "code_label": "Verification code",
            "verify_button": "Verify",
            "no_code_button": "I can't find the code",
            "error_expired": "The code has expired. Please try again.",
            "error_incorrect": "The code is incorrect. Please try again."
        },
        "mfa_setup_step": {
            "title": "Multi-Factor Authentication Setup",
            "setup_error": "An error occurred while setting up TOTP. Please try again.",
            "verification_error": "The verification code is incorrect. Please try again.",
            "finalize_error": "Verification succeeded, but we couldn't sign you in. Please try again.",
            "scan_instruction": "Scan this QR Code with your authenticator app:",
            "manual_entry_instruction": "Or enter the code below manually:",
            "code_input_instruction": "Enter the verification code:",
            "code_label": "Verification code",
            "verify_button": "Verify"
        },
        "password_step": {
            "title": "Password",
            "password_label": "Enter your password",
            "login_button": "Log in",
            "forgot_password_button": "Forgot password?",
            "error_invalid_password": "Invalid password. Please try again.",
            "error_mfa_verify": "An error occurred while verifying your account.",
            "error_send_email": "An error occurred while sending the email.",
            "error_send_reset_email": "Error sending email",
            "reset_email_sent": "A password reset email has been sent.",
            "sso_hint": "Your account uses Google or Microsoft login. Password login and password recovery are not available.",
            "welcome_verify_email": "Welcome to Medendo! 🚀 We'll first send you an <strong>email</strong> so you can set your own password.",
            "send_email_button": "Send email"
        }
    },
    "account_disabled": {
        "title": "Your account has been deactivated",
        "message": "You no longer have access to Medendo.\nIf you think this is a mistake, contact the person responsible for the connection with the Medendo system.",
        "logout_button": "Log out"
    },
    "signup": {
        "title": "Create your account",
        "subtitle": "Start your free trial of Medendo",
        "email_label": "Enter your email",
        "password_label": "Choose a password",
        "invalid_email": "Invalid email address",
        "weak_password": "The password must be at least {{min}} characters",
        "password_hint": "At least {{min}} characters",
        "send_code_button": "Send verification code",
        "code_sent": "We've sent a 6-digit code to {{email}}. Enter it below.",
        "redirecting_to_checkout": "Email verified, redirecting you to checkout...",
        "completing": "Finishing your account...",
        "complete_expired": "This checkout link has expired or wasn't completed. Please start again.",
        "code_label": "Verification code",
        "verify_code_button": "Verify",
        "invalid_code": "That code is incorrect or has expired",
        "resend_code": "Resend code",
        "resend_cooldown": "Resend in {{seconds}}s",
        "wrong_email": "Wrong email? Start over",
        "email_verified": "{{email}} is verified - now finish your account",
        "create_button": "Create account",
        "account_exists": "You already have an account with this email. Log in instead.",
        "already_completed": "Your account is already set up. Log in instead.",
        "unavailable": "Signup isn't available right now. Check back soon.",
        "have_account": "Already have an account?",
        "go_to_login": "Log in",
        "error_generic": "Something went wrong during sign-up. Please try again.",
        "checkout_cancelled": "Checkout was cancelled. You can try again whenever you're ready.",
        "rate_limited": "Too many attempts. Please wait a moment and try again.",
        "retry_button": "Try again",
        "first_name_label": "First name",
        "last_name_label": "Last name",
        "name_required": "Enter your first and last name",
        "toggle_password_visibility": "Show or hide the password",
        "trial_started": "Your trial has started.",
        "continue_button": "Continue"
    },
    "encounter": {
        "offline_too_long": "Offline too long, recording stopped. Click the \"regenerate\" button to create a report.",
        "undo": "Undo",
        "redo": "Redo",
        "name": "Name",
        "menu_record": "New record",
        "menu_history": "History",
        "menu_templates": "Templates",
        "menu_admin": "Admin",
        "menu_groups": "Groups",
        "menu_settings": "Settings",
        "menu_open": "Menu",
        "menu_close": "Close menu",
        "menu_integrations": "Integrations",
        "menu_support": "Support",
        "report": "Report",
        "report_field_empty": "Nothing recorded",
        "transcript_empty_title": "No transcript yet",
        "transcript_empty_body": "Start recording and the conversation will appear here as you talk",
        "report_empty_title": "No report yet",
        "report_empty_body": "Stop recording and the report will be written from the conversation",
        "report_empty_body_no_permission": "Your account can't create reports here",
        "report_generating_body": "Writing the report from the conversation…",
        "transcript": "Transcript",
        "template": "Template",
        "logout": "Logout",
        "logout_confirm_title": "Log out?",
        "logout_confirm_body": "You'll be signed out on this device and back at the login screen.",
        "logout_confirm": "Log out",
        "customer_portal": "To customer portal",
        "customer_portal_error": "Something went wrong opening the customer portal. Please try again.",
        "conversation_not_available": "This conversation is no longer available.",
        "loading": "Loading...",
        "select_patient": "Select client",
        "client_locked_by_assistant": "This record is linked to {{name}}. To work with another client, start a new record.",
        "client_locked_by_assistant_unnamed": "This record is linked to a client. To work with another client, start a new record.",
        "switch_to_patient_search": "Switch to client search",
        "no_patients_found": "No clients found",
        "no_patient": "No client - type a name",
        "patient_id_duplicate_hint": "Client ID is shown when name and date of birth are not unique.",
        "no_templates_message": "You need a template to start an encounter",
        "no_templates_subtitle": "Pick one above, or set one up in Templates.",
        "no_templates_action": "Go to templates",
        "no_care_plan_templates": "No templates available for this client",
        "care_plan_templates_load_error": "Could not load templates for this client. Please try again.",
        "care_plan_template_resolve_error": "Could not load the selected template. Please try again.",
        "in_person": "In person",
        "online": "Online",
        "meeting_type": "Meeting type",
        "start": "Start recording",
        "stop": "Stop recording",
        "recording": "Recording...",
        "resume": "Resume",
        "leave_while_recording_title": "Still recording",
        "leave_while_recording": "You're still recording. Leave this page and stop the recording?",
        "sticky_panel": "Sticky recording panel",
        "copied": "Copied",
        "unknown": "unknown",
        "submit_confirmation_title": "Confirm submission",
        "submit_confirmation_content": "This report will be sent to \"{{adapter}}\"{{patientName}}, are you sure?",
        "for_patient": " for client \"{{name}}\"",
        "select_patient_first": "Please select a client first",
        "send_report_to_adapter": "Send report to {{adapter}}",
        "cancel": "Cancel",
        "confirm": "Confirm",
        "audio_errors": {
            "access_denied": "Medendo could not access the audio source",
            "invalid_source": "The selected source cannot be used as audio input. Please choose another source.",
            "no_mic_found": "No microphone found on your device. Please check if your microphone is connected and try again",
            "mic_fallback_to_default": "Your selected microphone was not found. The default microphone is used instead. Select another one if needed.",
            "tab_capture_failed": "No audio can be captured for the selected tab. You may have chosen the wrong tab (no audio track there), or the browser is blocking audio capture from the selected tab.",
            "mic_not_supported": "This browser doesn't allow access to the microphone, so Medendo can't record here. Open Medendo in another browser and try again.",
            "start_recording_error": "An error occurred while starting the recording.",
            "upload_failed": "Error uploading audio fragment {{number}}",
            "low_audio_level": "The audio level has been low for a while. Please check your microphone.",
            "recording_not_capturing": "Nothing has been recorded for several minutes. Check your microphone and start the recording again.",
            "recording_stopped_early": "A recording fragment stopped earlier than expected. Check the transcript for gaps.",
            "audio_source_lost_microphone": "The microphone stopped working. Medendo will resume recording automatically once it's reconnected.",
            "audio_source_recovered_microphone": "The microphone is back. Recording continues automatically.",
            "audio_source_lost_screen": "The shared tab stopped sending audio. Share the tab again and start the recording again.",
            "audio_source_interrupted_microphone": "The microphone was interrupted. Another app may be using it.",
            "audio_source_interrupted_screen": "The audio from the shared tab was interrupted. Check that the tab is still playing and shared.",
            "audio_source_added": "A new microphone was connected. It is not used for this recording. Stop and start again to switch to it.",
            "recording_error": "Recording error occurred.",
            "start_recording_failed": "Failed to start recording.",
            "session_start_failed": "Failed to start an audio recording session. Check your microphone and/or update your browser.",
            "upload_missing": "No file uploaded.",
            "no_transcription": "No transcription received.",
            "transcription_failed": "Transcription failed."
        },
        "copy_transcript_tooltip": "Copy transcript text to clipboard",
        "copy_transcript": "Copy transcript",
        "modify_report_tooltip": "This allows you to make adjustments to the entire report",
        "modify_report": "Modify report",
        "modify_item_tooltip": "This allows changes to be made to this report element",
        "modify_item": "Modify item",
        "copy_report_tooltip": "Copy report text to clipboard",
        "copy_report": "Copy report",
        "regenerate_report": "Regenerate report",
        "report_actions": "Report actions",
        "transcript_actions": "Transcript actions",
        "send_report": "Send report",
        "submit_report": "Submit report to {{adapter}}",
        "confirm_edit": "Confirm edit",
        "delete": "Delete",
        "select_date_tooltip": "Select date",
        "copy_error": "Error copying",
        "autosave_saving": "Saving…",
        "autosave_saved": "Saved",
        "autosave_error": "Couldn't save your changes",
        "send_report_tooltip": "Send report",
        "send_needs_ehr_template": "This template isn't linked to {{adapter}}. Choose a {{adapter}} template to send the report.",
        "report_submitted_message": "Report submitted successfully to {{adapter}}",
        "recording_in_progress": "Recording in progress. The transcript updates regularly and shows who is speaking.",
        "next_update_in": "Next update in",
        "transcribing_last_audio": "Transcribing the last audio...",
        "participant": "Participant",
        "identifying": "Identifying speaker",
        "not_identified": "Unidentified voice",
        "ai_dialog_title": "What can I adjust for you?",
        "ai_dialog_placeholder": "Type here what you want to adjust...",
        "ai_dialog_input": "Adjustment instruction",
        "ai_dialog_start_recording": "Start voice input",
        "ai_dialog_stop_recording": "Stop voice input",
        "mic_access_denied": "Microphone access denied. Please check your browser settings.",
        "transcription_error": "An error occurred while transcribing the audio, please try again.",
        "processing_transcription": "Processing...",
        "select_microphone": "Select microphone",
        "microphone_label": "Microphone",
        "microphone_level": "Microphone level",
        "change_microphone_hint": "Stop the recording to change the microphone.",
        "default_microphone": "Default",
        "no_microphone_found": "No microphone found",
        "mic_permission": {
            "title": "Microphone Access Blocked",
            "instruction": "Microphone access for this site is blocked. Follow the steps below to enable it:",
            "step1": "Click on the lock icon to the left of the address bar.",
            "step2": "Choose Site settings.",
            "step3": "Search for Microphone in the list and set it to Allow.",
            "step4": "Reload this page to apply the settings.",
            "close": "Close",
            "system_title": "Your computer is blocking the microphone",
            "system_instruction": "Medendo can't reach a microphone. Most likely your computer doesn't let this browser use it.",
            "mac_step2": "In the list, turn on the switch next to your browser (for example Firefox or Chrome).",
            "windows_step2": "Switch on Microphone access and Let desktop apps access your microphone.",
            "other_step": "Allow this browser to use the microphone in your system settings.",
            "system_reload": "Reload this page. Still no microphone? Quit and reopen the browser.",
            "no_microphone_hint": "No microphone connected? Connect one and reload this page.",
            "open_settings": "Open settings",
            "how_to_fix": "How to fix",
            "mac_settings_path": "System Settings → Privacy & Security → Microphone",
            "windows_settings_path": "Settings → Privacy & security → Microphone",
            "open_or_go_to": "Click Open settings, or go to:"
        },
        "live_translation": {
            "title": "Live translation",
            "live": "Live",
            "toggle": "Translate live",
            "language_label_first": "First language",
            "language_label_second": "Second language",
            "start_recording": "Start recording to translate live.",
            "original": "Original",
            "translated": "Translated",
            "speaking": "Someone is speaking…",
            "error_refused": "Live translation was refused. It may be open somewhere else; close it there or try again later.",
            "error_unavailable": "Live translation is unavailable right now. Switch it off and on to try again.",
            "error_idle": "Live translation stopped because nobody spoke for a while. Switch it off and on to continue.",
            "error_microphone": "Live translation can't use the microphone in this browser."
        }
    },
    "chat": {
        "title": "AI Chat",
        "collapse": "Hide AI Chat",
        "expand": "Show AI Chat",
        "expand_new_reply": "Show AI Chat, new reply",
        "replying": "AI Chat is replying",
        "assistant_title": "Assistant",
        "busy_try_again": "Hang on. I'm still working on the last request.",
        "copy_message": "Copy message",
        "fullscreen": "Full screen",
        "exit_fullscreen": "Exit full screen",
        "dropzone_hint": "Drop files here to upload",
        "parsing_file": "Analyzing file content...",
        "placeholder": "Ask AI anything...",
        "attach_file": "Attach file",
        "send": "Send",
        "disclaimer": "AI can make mistakes. Please consult our usage guidelines.",
        "action_create_report": "Generating report",
        "action_modify_report": "Modifying report",
        "action_no_response": "No assistant response available for your request",
        "action_create_report_running": "Creating report...",
        "action_create_report_done": "Report created",
        "action_create_report_failed": "Report not created",
        "action_modify_report_running": "Updating report...",
        "action_modify_report_done": "Report updated",
        "action_modify_report_failed": "Report not updated",
        "changed_sections": "Updated: {{sections}}",
        "changed_nothing": "The report came back unchanged",
        "tool_other_running": "Using a tool...",
        "tool_other_done": "Used a tool",
        "tool_other_failed": "A tool failed",
        "tool_status_running": "in progress",
        "tool_status_done": "done",
        "tool_status_failed": "failed",
        "change_detected": "Encounter changed"
    },
    "auth": {
        "where_is_code_title": "Where is my code?",
        "code_explain_1": "To log in, you need a code from an Authenticator app. This is an app on your phone that generates an extra security code.",
        "most_used_apps": "The most used apps are:",
        "google_authenticator": "Google Authenticator",
        "microsoft_authenticator": "Microsoft Authenticator",
        "find_code_in_app": "Look for one of these apps on your phone and open it. You will see a list with one or more accounts. Look for a code with the name medendo.",
        "enter_code": "Enter the code next to medendo to continue.",
        "no_code_contact": "Can't find a code? Please contact our support. You will find a chat button in the bottom-right corner. Click it to get in touch.",
        "sign_up_with_google": "Sign up with Google",
        "log_in_with_google": "Log in with Google",
        "sign_up_with_microsoft": "Sign up with Microsoft",
        "log_in_with_microsoft": "Log in with Microsoft",
        "signin_failed": "Sign-in is not possible with the selected account.",
        "no_account_found": "No account found for that email. Sign up to get started.",
        "no_account_found_no_signup": "No account found for that email.",
        "password_setup": {
            "title": "Choose a new password",
            "password_label": "Enter a new password",
            "submit": "Set password",
            "reset_failed": "Failed to reset your password.",
            "success_message": "You can now log in with your new password.",
            "go_to_login": "Go to the login page"
        }
    },
    "history": {
        "retention_hint_one": "Deleted {{count}} day after the last change",
        "retention_hint_other": "Deleted {{count}} days after the last change",
        "sort_label": "Sort",
        "sort_newest": "Newest first",
        "sort_oldest": "Oldest first",
        "sort_expiring": "Expiring soonest",
        "sort_name": "Name A-Z",
        "sort_template": "Template A-Z",
        "sort_client": "Client A-Z",
        "expires_in_one": "Expires in {{count}} day",
        "expires_in_other": "Expires in {{count}} days",
        "no_client": "No client",
        "no_template": "No template",
        "record_count_one": "{{count}} record",
        "record_count_other": "{{count}} records",
        "today": "Today",
        "yesterday": "Yesterday",
        "expires_today": "Expires today",
        "expires_tomorrow": "Expires tomorrow",
        "search_placeholder": "Search...",
        "no_results": "No records match your search",
        "empty": "No records yet",
        "column_patient": "Client",
        "column_template": "Template",
        "column_date": "Date",
        "column_available_until": "Available until",
        "fetch_error": "An error occurred while fetching the history.",
        "delete_confirm_title": "Delete encounter?",
        "delete_confirm": "This cannot be undone.",
        "delete": "Delete",
        "cancel": "Cancel",
        "delete_error": "An error occurred while deleting the encounter.",
        "actions_tooltip": "Encounter actions",
        "offboarded_notice": "Your account is inactive, so you can't make new recordings. You can still read the reports and transcripts that haven't expired yet. Check the 'Available until' date.",
        "renew_hint": "Want to record again? Go to Settings to renew your subscription."
    },
    "admin": {
        "user_added_success": "User added successfully.",
        "add_invalid_input": "Email or name is invalid.",
        "user_exists": "User already exists.",
        "add_failed": "Failed to add user.",
        "user_updated_success": "User updated successfully.",
        "user_update_failed": "Failed to update user.",
        "column_email": "Email",
        "column_permissions": "Permissions",
        "column_status": "Status",
        "select_all_on_page": "Select all on this page",
        "previous_page": "Previous",
        "next_page": "Next",
        "page_of": "Page {{page}} of {{total}}",
        "add_user_title": "Add user",
        "email_label": "Email address",
        "given_name_label": "First name",
        "family_name_label": "Last name",
        "cancel": "Cancel",
        "add": "Add",
        "domain_mismatch_warning": "Warning: The domain of this email address differs from your domain ({{domain}}).",
        "invalid_email_error": "Please enter a valid email address",
        "user_already_exists_error": "This user already exists",
        "status_filter": "Status",
        "permission_filter": "Permission",
        "all": "All",
        "status_active": "Active",
        "status_inactive": "Inactive",
        "can_record": "Record",
        "can_template": "Templates",
        "can_org_admin": "Admin",
        "batch_onboard_btn": "Onboard users",
        "batch_offboard_btn": "Offboard users",
        "n_selected": "{{count}} selected",
        "delete_tooltip": "Remove",
        "clear_selection_tooltip": "Clear selection",
        "batch_status_success": "{{count}} users status changed to {{status}}.",
        "batch_status_error": "Error changing status.",
        "batch_status_partial": "Not all users could be updated.",
        "file_too_large_admin": "File is too large. Maximum size is 1MB.",
        "file_process_error": "Error processing file. Please try again.",
        "batch_onboard": "Onboard batch",
        "batch_offboard": "Offboard batch",
        "batch_operation": "Batch operation",
        "processing_file": "Processing file... This may take a moment.",
        "close": "Close",
        "no_users_in_file": "No users found in the file.",
        "success_count": "Success: {{count}}",
        "failed_count": "Failed: {{count}}",
        "confirm_n": "Confirm ({{count}})",
        "retry_failed_n": "Retry failed ({{count}})",
        "batch_delete_title": "Confirm removal",
        "batch_delete_content": "You are about to remove <strong>{{count}}</strong> {{userText}}. They will no longer have access to Medendo. Are you sure you want to remove them?",
        "batch_delete_user": "user",
        "batch_delete_users": "users",
        "batch_delete_confirm": "Remove",
        "fetch_error": "Error fetching users.",
        "permissions_updated_success": "Permissions updated successfully.",
        "permissions_update_error": "Error updating permissions.",
        "user_deactivated_success": "User status set to INACTIVE.",
        "user_activated_success": "User restored to ACTIVE.",
        "user_restore_error": "Error restoring user.",
        "no_users_selected": "No users selected.",
        "user_not_found": "User not found.",
        "unknown_error": "Unknown error.",
        "batch_delete_success": "{{count}} users removed.",
        "batch_delete_error": "Error removing users.",
        "batch_delete_partial": "Not all selected users could be removed.",
        "batch_permissions_success": "{{count}} users updated.",
        "batch_permissions_error": "Error updating permissions.",
        "batch_permissions_partial": "Not all users could be updated.",
        "status_update_error": "Error updating user status.",
        "status_active_label": "ACTIVE",
        "status_inactive_label": "INACTIVE",
        "batch_onboard_server_error": "An error occurred while adding the user."
    },
    "settings": {
        "title": "Settings",
        "language_label": "Language",
        "locale_update_error": "Failed to update language.",
        "preferences_title": "Preferences",
        "sounds_label": "Sound effects",
        "your_groups": "Your groups",
        "your_groups_empty": "You are not a member of any group yet.",
        "analytics_api_key": {
            "title": "Analytics API key",
            "info": "Use this key to authenticate against the Medendo usage analytics API. The full value is shown only once when the key is first created or right after a rotation. Rotating replaces the current key with a new one. The previous key is disabled.",
            "active_label": "Active key",
            "rotate": "Rotate",
            "store_warning": "Store this key now. It will not be shown again.",
            "copy": "Copy",
            "copied": "Analytics API key copied to clipboard.",
            "copy_failed": "Failed to copy analytics API key.",
            "load_failed": "Failed to load analytics API key status.",
            "action_failed": "Analytics API key action failed.",
            "missing_error": "No analytics API key is configured for your organization. Please contact support."
        },
        "account_title": "Account",
        "status_label": "Status",
        "status_inactive": "Inactive",
        "renew_explanation": "Your subscription has ended, so you can only read what you already recorded. Renew it to start recording again.",
        "renew_button": "Renew subscription",
        "renew_error": "Something went wrong starting your renewal. Please try again."
    },
    "offboard": {
        "title": "Deactivate user",
        "question": "Are you sure you want to deactivate this user?",
        "cancel": "Cancel",
        "confirm": "Confirm",
        "deleting": "Deactivating..."
    },
    "templates": {
        "sort_favorites": "Favorites first",
        "sort_name": "Name A-Z",
        "sort_recent": "Recently changed",
        "sort_author": "Author A-Z",
        "group_favorites": "Favorites",
        "group_all": "All templates",
        "template_count_one": "{{count}} template",
        "template_count_other": "{{count}} templates",
        "new": "New",
        "list_label": "Templates",
        "search_placeholder": "Search...",
        "sort_label": "Sort",
        "author_header": "Author",
        "shared_with_header": "Shared with",
        "modified_header": "Modified",
        "author_you": "You",
        "admins_only": "admins only",
        "no_results": "No templates match your search.",
        "favorite_tooltip": "Templates added to favorites are displayed first when selecting a template for a recording.",
        "manager": {
            "copy_suffix": " (copy)",
            "imported_suffix": " (imported)",
            "default_imported_name": "Imported template"
        },
        "copy_tooltip": "Copy",
        "download_tooltip": "Download",
        "edit_tooltip": "Edit",
        "delete_tooltip": "Delete",
        "delete_confirm_button": "Delete",
        "delete_confirm_title": "Delete template?",
        "delete_confirm": "This cannot be undone.",
        "cancel": "Cancel",
        "read_file_error": "Cannot read file content.",
        "import_success": "Template imported successfully.",
        "import_invalid": "Error importing template. Is it a valid JSON file?",
        "create_dialog": {
            "title": "Choose a way to create a template",
            "wizard": "Template Wizard",
            "import": "Import",
            "library": "Library",
            "manual": "Manual",
            "library_description": "Choose as many templates as you want from our library to use as a starting point.<br/><br/>You can experiment with these templates to see how the Medendo workspace works, and then easily adapt them to your specific needs and interests.",
            "library_button": "Choose from library",
            "wizard_description": "Upload a completed sample report as you would make yourself after a conversation with a client. Supported files: .docx, .pdf, .txt.<br/><br/>AI converts this into a template that you can use in Medendo. Check anonymity.",
            "wizard_button": "Upload example",
            "import_description": "Import an existing template from a .json file.",
            "import_button": "Upload .json",
            "manual_description": "You can also start with an empty template.<br/><br/>Choose this option if you don't have a good example document or don't want to use AI.",
            "manual_button": "Create yourself",
            "cancel": "Cancel"
        },
        "library": {
            "back": "Back",
            "title": "Template Library",
            "description": "This library is a collection of common templates from various healthcare disciplines. If you see a template you want to use, you can click the 'add' button to make it available in your account. Once the template is in your account, you can also freely edit it.",
            "tabs": {
                "external": "External"
            },
            "search_placeholder": "Search in library...",
            "locale_filter_all": "All languages",
            "language_label": "Language",
            "table": {
                "template": "Template",
                "description": "Description",
                "actions": "Actions"
            },
            "actions": {
                "add": "Add",
                "add_tooltip": "Add this template to your collection",
                "in_templates": "In your templates",
                "import_success": "Template \"{{name}}\" added successfully.",
                "import_error": "An error occurred while importing the template",
                "fetch_error": "An error occurred while fetching the template library."
            }
        },
        "external_library": {
            "search_placeholder": "Search in {{adapter}} library...",
            "search_placeholder_default": "adapter",
            "table": {
                "template": "Template",
                "description": "Description",
                "actions": "Actions"
            },
            "actions": {
                "add": "Add",
                "add_tooltip": "Add this template to your collection",
                "in_templates": "In your templates",
                "import_success": "Template \"{{name}}\" added successfully.",
                "import_error": "An error occurred while importing the template"
            }
        },
        "view": {
            "back": "Back",
            "download": "Download",
            "edit": "Edit",
            "name": "Name:",
            "description": "Description:",
            "no_description": "No description available",
            "items": "Items:",
            "no_description_fallback": "No description",
            "no_examples_fallback": "No examples"
        },
        "edit": {
            "back": "Back",
            "sync": "Synchronize with EHR",
            "save": "Save",
            "name_label": "Name",
            "description_label": "Description",
            "keywords_label": "Keywords",
            "keywords_info": "Add medical terms, names, or jargon that are hard to transcribe. They are passed to the speech-to-text engine to improve recognition accuracy for this template.",
            "keywords_placeholder": "e.g. quetiapine, sertraline, methylphenidate: separate with spaces, commas, periods or new lines",
            "items_header": "Items:",
            "add_item": "Add new item",
            "item_name": "Name",
            "item_type": "Type",
            "item_default_value": "Default text",
            "note_hint": "This text will appear in the report as-is. The AI will not modify it.",
            "item_description": "Description",
            "item_examples": "Examples",
            "delete_item_tooltip": "Delete item",
            "copy_item_tooltip": "Copy item",
            "move_up_tooltip": "Move up",
            "move_down_tooltip": "Move down",
            "copy_suffix": " (copy)",
            "sync_success": "{{count}} new items added",
            "sync_updated": "Template synchronized",
            "sync_updated_with_items": "Template synchronized, {{count}} new items added",
            "sync_up_to_date": "Template is already up-to-date",
            "sync_error": "Error syncing template.",
            "magic_wand": "Modify template",
            "magic_wand_tooltip": "Describe what you want to change and the AI will adjust the template",
            "modifying_ai": "The AI is adjusting the template…",
            "modify_success": "The AI updated the template",
            "modify_error": "Error modifying template.",
            "save_error_no_name": "Please provide a name for the template.",
            "save_error_no_items": "Please add at least one item.",
            "save_success_updated": "Template updated successfully",
            "save_success_created": "Template created successfully",
            "save_error_generic": "Error saving template.",
            "default_description": "A chronological report of the conversation with the client, patient and/or other persons in narrative form.",
            "types": {
                "text": "Text",
                "number": "Number",
                "date": "Date",
                "checkbox": "Checkbox",
                "radiobutton": "Radio Button",
                "checkbox_group": "Checkbox Group",
                "note": "Note"
            },
            "options_label": "Options",
            "add_option": "Add option",
            "option_placeholder": "Option",
            "duplicate_option": "This option already exists"
        },
        "share_manage": {
            "title": "Manage sharing",
            "not_shared": "Not shared",
            "all_members": "All members",
            "admins_only": "Admins only",
            "no_rights": "You don't have sharing rights in any group. Admins and template admins can share templates.",
            "private_summary": "This template is private to you.",
            "shared_summary_one": "Shared with {{count}} group.",
            "shared_summary_other": "Shared with {{count}} groups.",
            "cancel": "Cancel",
            "save": "Save"
        },
        "share_dialog": {
            "title": "Share template",
            "content": "Are you sure you want to share \"{{templateName}}\" with the group \"{{groupName}}\"?",
            "cancel": "Cancel",
            "confirm": "Share"
        },
        "unshare_dialog": {
            "title": "Stop sharing template",
            "content": "Are you sure you want to stop sharing \"{{templateName}}\" with the group \"{{groupName}}\"?",
            "cancel": "Cancel",
            "confirm": "Stop sharing"
        },
        "n_groups": "{{count}} groups",
        "created_by": "Created by {{creator}}",
        "modified_ago": "modified {{time}}",
        "created_modified_tooltip": "Created {{created}} · last modified {{modified}}",
        "created_date": "Created {{date}}",
        "actions_tooltip": "Template actions",
        "wizard_loading": {
            "elapsed_time": "Elapsed time: {{time}}",
            "please_wait": "A few minutes of patience please...",
            "messages": [
                "Uploading file ...",
                "Reading document ...",
                "Analysing content ...",
                "Generating template ...",
                "Almost ready ..."
            ]
        }
    },
    "adapter": {
        "connection_error": "{{adapter}} is temporarily unavailable. Please try again later.",
        "reconnected": "Reconnected to {{adapter}} successfully.",
        "reconnect_required": "Your {{adapter}} session has expired. Please reconnect.",
        "reconnect_failed": "Could not reconnect to {{adapter}}. Please reconnect from Integrations.",
        "fetch_templates_error": "Could not load templates from {{adapter}}.",
        "fetch_patients_error": "Could not load clients from {{adapter}}.",
        "required_fields_one": "The field {{fields}} is mandatory and needs to be filled.",
        "required_fields_other": "The fields {{fields}} are mandatory and need to be filled."
    },
    "integrations": {
        "load_error": "Could not load integrations",
        "init_error": "Could not start connection",
        "launch": {
            "session_expired": "Session expired. Please reopen Medendo from your EHR.",
            "config_error": "Could not establish EHR session. Please contact support."
        },
        "disconnect_confirm_title": "Disconnect {{adapter}}?",
        "disconnect_confirm": "You can reconnect at any time.",
        "cancel": "Cancel",
        "disconnect_success": "Connection disconnected",
        "disconnect_error": "Could not disconnect",
        "connected": "Connected",
        "not_connected": "Not connected",
        "disconnect_btn": "Disconnect",
        "connect_btn": "Connect",
        "callback": {
            "invalid_parameters": "Invalid callback parameters",
            "success": "Integration connected successfully!",
            "error": "Could not complete integration",
            "redirecting": "You are being redirected to the integrations page...",
            "connecting": "Connecting integration...",
            "please_wait": "Please wait while we configure your integration."
        }
    },
    "errors": {
        "create_encounter": "An error occurred while creating the encounter.",
        "update_encounter": "An error occurred while updating the encounter.",
        "client_locked": "The client can't be changed anymore: the assistant already collected their history.",
        "generate_report": "An error occurred while generating the report.",
        "generate_transcript": "An error occurred while generating the transcript.",
        "update_field": "An error occurred while updating the field.",
        "upload_file": "An error occurred while uploading the file.",
        "delete_file": "An error occurred while deleting the file.",
        "send_message": "An error occurred while sending the message.",
        "submit_report": "An error occurred while submitting the report.",
        "fetch_templates": "An error occurred while fetching the templates.",
        "fetch_groups": "An error occurred while fetching the groups.",
        "copy_template": "An error occurred while copying the template.",
        "delete_template": "An error occurred while deleting the template.",
        "sharing_updated": "Sharing updated.",
        "update_sharing": "An error occurred while updating sharing.",
        "logout": "An error occurred while logging out.",
        "user_info": "An error occurred while fetching user information.",
        "not_found_encounter": "Encounter could not be found.",
        "azure_policy_violation": "Modification failed due to Azure OpenAI content policy. Please try again. If it fails again, contact support.",
        "restricted_browser_environment": "It looks like you opened our app in a restricted browser environment. Please open the app in your browser.",
        "item_missing_in_template": "The selected item is missing in the template and cannot be modified (the template may have changed).",
        "file_too_large": "File \"{{name}}\" is too large. It must not exceed {{maxMb}} MB.",
        "upload_success": "File \"{{name}}\" uploaded successfully.",
        "process_file_problem": "There was a problem processing \"{{name}}\".",
        "template_not_found": "The requested template ({{id}}) is not available to you. Contact the person responsible for the connection with the Medendo system.",
        "patient_not_found": "The client from the launch ({{id}}) is not available to you. Contact the person responsible for the connection with the Medendo system.",
        "update_favorite_failed": "Failed to update favorite status.",
        "template_deleted": "Template deleted successfully.",
        "copy_success": "Template copied successfully.",
        "file_deleted_success": "File \"{{name}}\" deleted successfully.",
        "process_file_delete_problem": "There was a problem deleting \"{{name}}\".",
        "unauthorized_refresh": "Unauthorized request. Trying to refresh token...",
        "error_page": {
            "title": "Oops! Something went wrong.",
            "default_message": "The page you are looking for cannot be found or another error occurred.",
            "unexpected_message": "An unexpected error occurred. Go back to the home page and try again.",
            "back_home": "Back to the home page"
        }
    },
    "groups": {
        "title": "Groups",
        "create_title": "Create group",
        "name_label": "Group name",
        "description_label": "Description (optional)",
        "create_admin_note": "You'll be the group's first admin. Add members and share templates once it's created.",
        "create": "Create",
        "cancel": "Cancel",
        "create_error": "An error occurred while creating the group.",
        "created": "Group \"{{name}}\" created.",
        "no_groups": "No groups available.",
        "no_users_found": "No users found.",
        "search_more_hint": "Showing first {{count}} results. Refine your search to see more.",
        "tab_templates": "Templates",
        "template_visibility_group": "All members",
        "template_visibility_admin": "Group admins",
        "template_visibility_private": "Private",
        "no_templates": "No templates shared with this group yet.",
        "description": "Teams in your organisation that share templates.",
        "search_placeholder": "Search groups…",
        "search_members_placeholder": "Search members…",
        "all_groups": "All groups",
        "new_group": "New group",
        "scope_group_admins_hint": "Visible to group admins only.",
        "view_only_hint": "You can view this group's members. Editing is limited to group and org admins.",
        "member_role_admin": "Admin",
        "member_role_member": "Member",
        "add_member": "Add member",
        "add_member_title": "Add member",
        "add_member_search_label": "Search user",
        "add": "Add",
        "no_members": "No members in this group.",
        "fetch_members_error": "An error occurred while fetching members.",
        "add_member_error": "An error occurred while adding the member.",
        "remove_member_error": "An error occurred while removing the member.",
        "update_member_error": "An error occurred while updating the member.",
        "member_added": "Member added.",
        "member_removed": "Member removed.",
        "member_updated": "Member permissions updated.",
        "scope_change_tooltip": "Change who can see this",
        "open_template_tooltip": "Open template",
        "integration_tooltip": "Belongs to the {{name}} integration",
        "member_actions_tooltip": "Member actions",
        "remove_from_group": "Remove from group",
        "deleted": "Group deleted.",
        "delete_error": "An error occurred while deleting the group.",
        "edit_title": "Edit group",
        "save_changes": "Save changes",
        "edit_hint": "Members and templates are not affected: only how the group appears.",
        "updated": "Group details updated.",
        "update_error": "An error occurred while updating the group.",
        "add_member_note": "New members join as Member. Only an org admin can make someone a group admin.",
        "member_you": "you",
        "delete_dialog_title": "Delete group '{{name}}'?",
        "delete_body": "Templates assigned to it will become private to their owners. This cannot be undone.",
        "delete_affected_one": "1 shared template and {{memberCount}} memberships will be affected.",
        "delete_affected_other": "{{count}} shared templates and {{memberCount}} memberships will be affected.",
        "delete_confirm_button": "Delete group",
        "n_shared_one": "1 shared template",
        "n_shared_other": "{{count}} shared templates",
        "private_templates_section": "Your private templates",
        "members_section_title": "Members",
        "edit_action": "Edit",
        "n_members_one": "1 member",
        "n_members_other": "{{count}} members",
        "n_admins_one": "1 admin",
        "n_admins_other": "{{count}} admins",
        "n_template_admins_one": "1 template admin",
        "n_template_admins_other": "{{count}} template admins",
        "role_template_admin": "Template admin",
        "make_admin": "Make admin",
        "make_template_admin": "Make template admin",
        "make_member": "Set role to Member",
        "set_role_admin_title": "Make admin?",
        "set_role_admin_body": "They'll be able to add or remove members, set roles, and manage template sharing for this group.",
        "set_role_admin_confirm": "Make admin",
        "set_role_template_admin_title": "Make template admin?",
        "set_role_template_admin_body": "They'll be able to share and unshare templates with this group, on top of regular membership.",
        "set_role_template_admin_confirm": "Make template admin",
        "set_role_member_title": "Set as member?",
        "set_role_member_body": "They'll keep their membership but can only view the group and its shared templates.",
        "set_role_member_confirm": "Set as member",
        "no_matches": "No matches",
        "no_results": "No results"
    },
    "privacy_policy": {
        "title": "Medendo Privacy Statement",
        "agree_checkbox": "I agree to the privacy statement",
        "agree_button": "Agree"
    },
    "usage_instructions": {
        "title": "Instructions for responsible use of Medendo",
        "agree_checkbox": "I have read and understood the general instructions",
        "agree_button": "Agree"
    },
    "user_agreement": {
        "title": "General terms and conditions for the use of Medendo",
        "agree_checkbox": "I agree to the General Terms and Conditions",
        "agree_button": "Agree"
    }
}
